import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { ALL_AXES, Axis } from '@core';
import { ParameterNode, UserGuide } from '@data';
import {
    parametersAtom,
    selectedActiveAnalysisAtom,
    swingGuideAxesAtom,
    swingGuideTimeSeriesAtom,
    uiNodeBySmpIdAtom,
} from '@store';

export const useSwingGuideModel = (selectedSmpId: number) => {
    const parameters = useAtomValue(parametersAtom) ?? [];
    const parameterNodesByLookup = useMemo(() => indexParameterNodes(parameters), [parameters]);
    const uiNodesBySmpId = useAtomValue(uiNodeBySmpIdAtom);
    const guideAxesByTimeSeries = useAtomValue(swingGuideAxesAtom);
    const guidesByTimeSeries = useAtomValue(swingGuideTimeSeriesAtom);
    const activeAnalysis = useAtomValue(selectedActiveAnalysisAtom);
    const selectedParameterNode = uiNodesBySmpId[selectedSmpId];
    const selectedTimeSeriesId = selectedParameterNode?.parameter.timeSeriesId;
    const measurement = activeAnalysis?.data.analysis?.measurements?.find(
        ({ measurement_name }) => measurement_name === selectedTimeSeriesId,
    );
    const guide = measurement ? guidesByTimeSeries.get(measurement.measurement_id) : undefined;
    const guideAxes = guide ? guideAxesByTimeSeries.get(guide.timeSeriesMeasurementId) ?? [] : [];
    const availableGuideAxes = useMemo(() => {
        if (selectedTimeSeriesId === undefined) return [];

        return guideAxes.flatMap((axis) => {
            const axisParameters = getGuideAxisParameters(axis, selectedTimeSeriesId, parameterNodesByLookup);
            return axisParameters.length ? [{ ...axis, parameters: axisParameters }] : [];
        });
    }, [guideAxes, parameterNodesByLookup, selectedTimeSeriesId]);

    if (!selectedParameterNode || selectedTimeSeriesId === undefined || !guide) return null;

    const selectedGuideAxis = availableGuideAxes.find(
        (axis) => selectedParameterNode.parameter.axis === ALL_AXES[axis.axis],
    );
    if (!selectedGuideAxis) return null;

    const selectedGuideParameter = selectedGuideAxis.parameters.find(
        ({ smpId: parameterSmpId }) => parameterSmpId === selectedSmpId,
    );
    if (!selectedGuideParameter) return null;

    return {
        selectedParameterNode,
        guide,
        selectedGuideAxis,
        selectedGuideParameter,
        availableGuideAxes,
    };
};

type GuideAxis = UserGuide[number]['axes'][number];
type GuideAxisParameter = GuideAxis['parameters'][number] & { smpId: number };
type ParameterNodeIndex = Record<string, Record<string, Record<string, ParameterNode>>>;

const indexParameterNodes = (parameters: ParameterNode[]): ParameterNodeIndex =>
    parameters.reduce<ParameterNodeIndex>((index, parameterNode) => {
        const { timeSeriesId, axis, position } = parameterNode.parameter;
        if (timeSeriesId === undefined || !axis || !position) return index;

        const timeSeriesKey = timeSeriesId ?? '';
        index[timeSeriesKey] ??= {};
        index[timeSeriesKey][axis] ??= {};
        index[timeSeriesKey][axis][position] = parameterNode;
        return index;
    }, {});

const getGuideAxisParameters = (
    axis: GuideAxis,
    timeSeriesId: string | null,
    parameterNodesByLookup: ParameterNodeIndex,
): GuideAxisParameter[] =>
    axis.parameters.flatMap((axisParameter) => {
        const axisName = ALL_AXES[axis.axis] as Axis;
        const parameterNode = parameterNodesByLookup[timeSeriesId ?? '']?.[axisName]?.[`p${axisParameter.pPosition}`];

        return parameterNode ? [{ ...axisParameter, smpId: parameterNode.parameter.smpId }] : [];
    });
